import type { Plan } from "../../../shared/goals/plan";
import type { LogEntry } from "./runEvents";

/**
 * A planning run as the prototype shows it: one step per stage the run reached, each told in plain words
 * from what the run really did and, once it is saved, from the plan itself.
 */
export const PSUBS = [
  "Reading the playbook, the product and the skills",
  "Checking what earlier goals taught",
  "Researching the market",
  "Checking the plan against the playbook",
  "Saving the plan for you",
] as const;
export const STEP_ICONS = ["doc", "spark", "search", "flask", "user"] as const;

export interface PlanStep {
  g: number;
  done: boolean;
  title: string;
  res?: string;
  lessons?: string[];
  tag?: [string, string];
  t: string;
  entries: LogEntry[];
}

const many = (n: number, one: string, more: string) => `${n} ${n === 1 ? one : more}`;

function stageOfTool(tool: string): number {
  if (tool === "read") return 0;
  if (tool === "list_lessons") return 1;
  if (tool === "save_plan") return 4;
  return 2;
}

/** Which stage each log entry belongs to. Stages only move forward; the thoughts just before saving are the Plan stage. */
export function stagesOf(log: LogEntry[]): number[] {
  let cur = 0;
  const g = log.map((e) => {
    if (e.tool) cur = Math.max(cur, stageOfTool(e.tool));
    return cur;
  });
  const save = log.findIndex((e) => e.tool === "save_plan");
  for (let k = save - 1; k >= 0 && !log[k].tool; k--) g[k] = 3;
  return g;
}

interface Ctx {
  running: boolean;
  plan: Plan | null;
  playbookTitle: string | null;
  skillNames: string[];
  approved: boolean;
}

export function planSteps(log: LogEntry[], ctx: Ctx): PlanStep[] {
  const g = stagesOf(log);
  const at = (stage: number) => log.filter((_, k) => g[k] === stage);
  const save = log.find((e) => e.tool === "save_plan");
  const top = log.length ? Math.max(...g) : -1;
  const steps: PlanStep[] = [];

  for (let stage = 0; stage <= top; stage++) {
    const entries = at(stage);
    if (!entries.length && !(stage === 3 && save)) continue;
    const last = entries[entries.length - 1] ?? save;
    const done = !ctx.running || stage < top;
    const step: PlanStep = { g: stage, done, title: "", t: last?.t ?? "", entries };
    if (stage === 0) {
      const names = new Set(entries.map((e) => String((e.i as { path?: string } | undefined)?.path ?? "").match(/skills\/([^/]+)\/SKILL\.md$/)?.[1]).filter(Boolean));
      const skills = ctx.skillNames.filter((n) => names.has(n)).length;
      const pb = names.has("playbook");
      step.title = pb
        ? skills
          ? `Read the playbook and ${many(skills, "skill", "skills")}`
          : "Read the playbook"
        : skills
          ? `Read ${many(skills, "skill", "skills")}`
          : entries.some((e) => e.tool)
            ? "Read the product and the goal"
            : "Getting started";
      if (pb && ctx.playbookTitle) step.res = `Following “${ctx.playbookTitle}”. The playbook comes first; the skills say how each channel is run.`;
    } else if (stage === 1) {
      step.title = "Read what earlier goals taught";
      const ids = ctx.plan?.lessons_used.map((l) => l.id) ?? [];
      step.res = ids.length ? `Only accepted lessons count. The plan cites ${ids.length}: ${ids.join(", ")}.` : "Only accepted lessons count.";
      if (ids.length) step.lessons = ids;
    } else if (stage === 2) {
      const calls = entries.filter((e) => e.tool);
      const searches = calls.filter((e) => e.tool === "web_search").length;
      const reads = calls.filter((e) => e.tool === "read_page").length;
      const lookups = calls.length - searches - reads;
      step.title = "Researched the market";
      step.res =
        `${many(searches, "search", "searches")}, ${many(reads, "page", "pages")} read, ${many(lookups, "data lookup", "data lookups")}.` +
        (ctx.plan ? ` ${ctx.plan.findings.length} findings kept, each with its source.` : "");
    } else if (stage === 3) {
      step.title = "Checked the plan against the playbook";
      if (ctx.plan) {
        const c = ctx.plan.conflicts.length;
        step.res = `Follows ${many(ctx.plan.playbook_followed.length, "playbook rule", "playbook rules")}.${c ? ` Disagrees with ${c}, and says why instead of quietly changing them.` : ""}`;
      }
    } else {
      step.title = "Saved the plan for your review";
      step.res = "Nothing runs until you approve it.";
      if (!ctx.approved) step.tag = ["you", "Needs you"];
    }
    steps.push(step);
  }
  return steps;
}
